/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactNode} from 'react';
import Button, {ButtonProps} from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '../../../types/types';

export interface PrimaryButtonProps extends ButtonProps {
  children: ReactNode;
  padding?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  display?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    width     : ({fullWidth}: {fullWidth: boolean}) => fullWidth ? '100%' : 'auto',
    padding   : ({padding}: {padding: string}) => padding || '5px',
    display   : ({display}: {display: string}) => display || {},
    '&:focus' : {
      outline : `2px solid ${dlsColor('caBlue')}`
    }
  },
  disabledContainer : {
    outline : 'none',
    padding : ({padding}: {padding: string}) => padding || '5px',
    display : ({display}: {display: string}) => display || {},
    width   : ({fullWidth}: {fullWidth: boolean}) => fullWidth ? '100%' : 'auto'
  },
  root : {
    backgroundImage    : `linear-gradient(-75deg, ${dlsColor('grey1')} 50%, ${dlsAlt('components', 'btn', 'activePrimaryColor')} 50%)`,
    backgroundPosition : '100%',
    backgroundSize     : '300%',
    transition         : 'background .5s ease-out',
    background         : dlsColor('grey1'),
    color              : dlsColor('white'),
    fontSize           : theme.typography.fontSize,
    lineHeight         : '24px',
    boxShadow          : 'none',
    '&:hover'          : {
      backgroundPosition : 0,
      boxShadow          : 'none',
      color              : dlsColor('white')
    },
    '&:active' : {
      backgroundImage : `linear-gradient(-75deg, ${dlsColor('grey1')} 50%, ${dlsAlt('components', 'btn', 'activePrimaryColor')} 50%)`
    },
    '&$disabled' : {
      color           : dlsAlt('components', 'btn', 'disabledPrimaryColor'),
      backgroundImage : 'none'
    }
  },
  rootAlt : {
    width : '100%'
  },
  disabled : {}
}));

/**
 *
 * @PrimaryButton
 *
 * Primary buttons are used to convey the most urgent or main actions on a particular
 * page  . In cards, use the regular primary buttons. If paired, use it with the
 * tertiary button.
 *
 * ```typescript
 *
 * import React from 'react';
 * import { PrimaryButton } from '@ion/dls/src/components/buttons/';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <PrimaryButton>Default</PrimaryButton>
 *       <br />
 *       <PrimaryButton disabled>Disabled</PrimaryButton>
 *       <br />
 *       <PrimaryButton fullWidth>Full Width</PrimaryButton>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
const PrimaryButton = ({children, padding, display, fullWidth, ...props}: PrimaryButtonProps) => {
  const classes = useStyles({padding, fullWidth, display});
  return (
    <div className={props.disabled ? classes.disabledContainer : classes.container}
      tabIndex={props.disabled ? -1 : 0}>
      <Button variant='contained'
        tabIndex={-1}
        classes={{
          root     : classnames(classes.root, fullWidth ? classes.rootAlt : null),
          disabled : classes.disabled
        }}
        disableRipple
        disableFocusRipple
        color='primary'
        {...props}>
        {children}
      </Button>
    </div>
  );
};

export {PrimaryButton};
